import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject } from '@angular/core';
import { AccountService } from '../infra/account.service';
import { pipe, switchMap, tap } from 'rxjs';
import { IOwnerSettings } from '../entities/account.model';
import { tapResponse } from '@ngrx/operators';
import { ELogoKeys, ELogoSizes } from '../entities/logos.enum';
import { IFileModel } from '@shared/filegroup/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';

export interface ILogosStore {
  logoRemoved: string,
  logoUpdated: string,
  square: IFileModel,
  print: IFileModel,
  rectangular: IFileModel,
}

export const logosInitialStore: any = {
  logoRemoved: null,
  logoUpdated: null,
  square: null,
  print: null,
  rectangular: null,
};

export type LogosStore = InstanceType<typeof LogosStore>;

export const LogosStore = signalStore(
  { providedIn: 'root' },
  withState<ILogosStore>(logosInitialStore),
  withMethods((store, accService = inject(AccountService), errorStore = inject(ErrorStore)) => {

    return {
      loadAll: rxMethod<void>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getAllLogos') })),
        switchMap(() => accService.getAllLogos().pipe(
          tapResponse({
            next: (response) => {
              patchState(store,
                {
                  square: response.SquareLogoFile,
                  rectangular: response.RectangularLogoFile,
                  print: response.PrintLogoFile,
                  ...setPropLoaded('getAllLogos')
                })
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getAllLogos', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      loadByTenantId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('GetLogosByTenantId') })),
        switchMap((tenantId) => {
          return accService.GetLogosByTenantId(tenantId).pipe(
            tapResponse({
              next: (response) => {
                patchState(store,
                  {
                    square: response.SquareLogoFile,
                    rectangular: response.RectangularLogoFile,
                    print: response.PrintLogoFile,
                    ...setPropLoaded('GetLogosByTenantId')
                  })
              },
              error: (error: HttpErrorResponse) => {
                console.error(error);
                patchState( store, { ...setPropError('GetLogosByTenantId', error) });
                errorStore.setHttpError(error)
              }
            }),
          )
        })
      )),

      update: rxMethod<{logo: Partial<IFileModel>, size: ELogoSizes}>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateOwnerSettings') })),
        switchMap(({logo, size}) => {
          const _logo: any = structuredClone(logo);
          const _store: Partial<IOwnerSettings> = {
            Logos: {
              [ELogoKeys.RECTANGULAR]: size === ELogoSizes.RECTANGULAR ? _logo : store.rectangular(),
              [ELogoKeys.SQUARE]: size === ELogoSizes.SQUARE ? _logo : store.square(),
              [ELogoKeys.PRINT]: size === ELogoSizes.PRINT ? _logo : store.print(),
            }
          }

          return accService.updateOwnerSettings(_store).pipe(
            tapResponse({
              next: () => {
                patchState(store, { [`${size}`]: logo, logoUpdated: `${size}`, ...setPropLoaded('updateOwnerSettings') });
              },
              error: (error: HttpErrorResponse) => {
                patchState( store, { ...setPropError('updateOwnerSettings', error) });
              }
            }),
          )
        }
        )
      )),

      delete: rxMethod<{logo: Partial<IFileModel>, size: ELogoSizes}>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteOwnerSettings') })),
        switchMap(({logo, size}) => {
          const _store: Partial<IOwnerSettings> = {
            Logos: {
              [ELogoKeys.RECTANGULAR]: size === ELogoSizes.RECTANGULAR ? null : store.rectangular(),
              [ELogoKeys.SQUARE]: size === ELogoSizes.SQUARE ? null : store.square(),
              [ELogoKeys.PRINT]: size === ELogoSizes.PRINT ? null : store.print(),
            }
          }
          return accService.updateOwnerSettings(_store).pipe(
            tapResponse({
              next: () => {
                patchState(store, { [`${size}`]: null, logoRemoved: `${size}`, ...setPropLoaded('deleteOwnerSettings')})
              },
              error: (error: HttpErrorResponse) => {
                patchState( store, { ...setPropError('deleteOwnerSettings', error) });
              }
            }),
          )
        }
        )
      )),

      loadLogosByTenantId: rxMethod(pipe(
        switchMap(() => accService.getAllLogos().pipe(
          tapResponse({
            next: (response) =>
              patchState(store,
                {
                  square: response.SquareLogoFile,
                  rectangular: response.RectangularLogoFile,
                  print: response.PrintLogoFile,
                }),
            error: (error: HttpErrorResponse) => errorStore.setHttpError(error)
          }),
        ))
      )),

      blankPropState: (props: string[]) => {
        patchState(store, { ...setPropInit(...props) });
      },

      blankLogoState: () => {
        patchState(store, {
          logoRemoved: null,
          logoUpdated: null,
          ...setPropInit('updateOwnerSettings', 'deleteOwnerSettings'),
        });
      }
    }
  }),
  withReqState('getAllLogos', 'GetLogosByTenantId', 'updateOwnerSettings', 'deleteOwnerSettings')
);


